import { useCallback, useState } from 'react';
import type React from 'react';
import type { TableColumnType, TableProps } from 'antd';
import { Button, Input, InputNumber, Select, Space, Table } from 'antd';
import type { FilterDropdownProps } from 'antd/es/table/interface';
import {
  FilterFilled,
  FilterOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import Highlighter from 'react-highlight-words';
import { useTranslation } from 'react-i18next';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

const ACTIVE_COLOR = '#1677ff';

/**
 * An antd column plus an optional exportValue, used by the Excel export to
 * write the same readable value the cell shows (labels, formatted dates).
 * Without it, the export writes the raw field value.
 */
export type SummaryColumn<T> = TableColumnType<T> & {
  exportValue?: (record: T) => string | number;
};

// ---------- Text search ----------

interface SearchFilterDropdownProps extends FilterDropdownProps {
  onSearch: (text: string) => void;
}

const SearchFilterDropdown = ({
  setSelectedKeys,
  selectedKeys,
  confirm,
  clearFilters,
  close,
  onSearch,
}: SearchFilterDropdownProps) => {
  const { t } = useTranslation();

  const search = (closeDropdown = true) => {
    confirm({ closeDropdown });
    onSearch(String(selectedKeys[0] ?? ''));
  };

  return (
    <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
      <Input
        autoFocus
        placeholder={`${t('convSignComp.search')}...`}
        value={selectedKeys[0] as string}
        onChange={(e) =>
          setSelectedKeys(e.target.value ? [e.target.value] : [])
        }
        onPressEnter={() => search()}
        style={{ marginBottom: 8, display: 'block' }}
      />
      <Space>
        <Button
          type="primary"
          onClick={() => search()}
          icon={<SearchOutlined />}
          size="small"
          style={{ width: 90 }}
        >
          {t('convSignComp.search')}
        </Button>
        <Button
          onClick={() => {
            clearFilters?.();
            onSearch('');
          }}
          size="small"
          style={{ width: 90 }}
        >
          {t('convSignComp.reset')}
        </Button>
        <Button type="link" size="small" onClick={() => search(false)}>
          {t('convSignComp.filter')}
        </Button>
        <Button type="link" size="small" onClick={() => close()}>
          {t('convSignComp.close')}
        </Button>
      </Space>
    </div>
  );
};

/**
 * Returns a function that builds text-search column props with highlighting.
 * Holds the search/highlight state, so call it once per table.
 */
export const useColumnSearch = <T extends object>() => {
  const [searchText, setSearchText] = useState('');
  const [searchedColumn, setSearchedColumn] = useState<keyof T | null>(null);

  return useCallback(
    (dataIndex: keyof T & string): SummaryColumn<T> => ({
      filterDropdown: (props) => (
        <SearchFilterDropdown
          {...props}
          onSearch={(text) => {
            setSearchText(text);
            setSearchedColumn(dataIndex);
          }}
        />
      ),
      filterIcon: (filtered: boolean) => (
        <SearchOutlined
          style={{ color: filtered ? ACTIVE_COLOR : undefined }}
        />
      ),
      onFilter: (value, record) =>
        String(record[dataIndex] ?? '')
          .toLowerCase()
          .includes(String(value).toLowerCase()),
      sorter: (a, b) =>
        String(a[dataIndex] ?? '').localeCompare(String(b[dataIndex] ?? '')),
      render: (text) =>
        searchedColumn === dataIndex ? (
          <Highlighter
            highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
            searchWords={[searchText]}
            autoEscape
            textToHighlight={text ? text.toString() : ''}
          />
        ) : (
          text
        ),
    }),
    [searchText, searchedColumn]
  );
};

// ---------- Checkbox list ----------

/**
 * Checkbox-list filter built from the distinct values in the data.
 * Pass formatValue to show a label instead of the raw value (e.g. enum
 * names); it's used for both the cells and the filter options.
 */
export const listFilterProps = <T extends object, K extends keyof T = keyof T>(
  rows: T[],
  dataIndex: K,
  formatValue?: (value: T[K]) => string
): SummaryColumn<T> => {
  const label = (value: T[K]): string =>
    formatValue ? formatValue(value) : String(value ?? '');

  return {
    filters: Array.from(new Set(rows.map((r) => r[dataIndex])))
      .filter((v) => v !== undefined && v !== null && v !== '')
      .map((v) => ({ text: label(v), value: String(v) }))
      .sort((a, b) => a.text.localeCompare(b.text)),
    onFilter: (value, record) => String(record[dataIndex]) === value,
    sorter: (a, b) => label(a[dataIndex]).localeCompare(label(b[dataIndex])),
    filterIcon: (filtered: boolean) => (
      <FilterFilled
        style={{ fontSize: '20px', color: filtered ? ACTIVE_COLOR : undefined }}
      />
    ),
    filterSearch: true,
    ...(formatValue && {
      render: (value: T[K]) => formatValue(value),
      exportValue: (record: T) => formatValue(record[dataIndex]),
    }),
  };
};

// ---------- Operator + number ----------

type Operator = '=' | '>' | '>=' | '<' | '<=';
interface NumberFilterValue {
  operator: Operator;
  num: string;
}
const OPERATORS: Operator[] = ['=', '>', '>=', '<', '<='];
const DEFAULT_NUMBER_FILTER: NumberFilterValue = { operator: '=', num: '' };

const parseNumberFilter = (key: React.Key | undefined): NumberFilterValue =>
  key ? (JSON.parse(String(key)) as NumberFilterValue) : DEFAULT_NUMBER_FILTER;

const NumberFilterDropdown = ({
  setSelectedKeys,
  selectedKeys,
  confirm,
  clearFilters,
}: FilterDropdownProps) => {
  const { t } = useTranslation();
  const current = parseNumberFilter(selectedKeys[0]);
  const update = (next: NumberFilterValue) =>
    setSelectedKeys([JSON.stringify(next)]);

  return (
    <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
      <Space>
        <Select<Operator>
          style={{ width: 65 }}
          value={current.operator}
          options={OPERATORS.map((op) => ({ label: op, value: op }))}
          onChange={(operator) => update({ ...current, operator })}
        />
        <InputNumber
          placeholder="val"
          value={current.num === '' ? null : Number(current.num)}
          onChange={(val) =>
            update({ ...current, num: val === null ? '' : String(val) })
          }
          onPressEnter={() => confirm()}
        />
      </Space>
      <br />
      <Space align="center" style={{ paddingTop: 8 }}>
        <Button size="small" type="primary" onClick={() => confirm()}>
          {t('summaryTable.filter')}
        </Button>
        <Button
          size="small"
          onClick={() => {
            clearFilters?.();
            confirm();
          }}
        >
          {t('summaryTable.refresh')}
        </Button>
      </Space>
    </div>
  );
};

/** Operator + number filter; each column keeps its own value. */
export const numberFilterProps = <T extends object>(
  dataIndex: keyof T
): SummaryColumn<T> => ({
  filterDropdown: (props) => <NumberFilterDropdown {...props} />,
  filterIcon: (filtered: boolean) => (
    <FilterOutlined
      style={{ fontSize: '20px', color: filtered ? ACTIVE_COLOR : undefined }}
    />
  ),
  onFilter: (value, record) => {
    const { operator, num } = parseNumberFilter(value as React.Key);
    if (num === '') return true;
    const target = Number(num);
    const recordValue = Number(record[dataIndex]);
    switch (operator) {
      case '=':
        return recordValue === target;
      case '>':
        return recordValue > target;
      case '>=':
        return recordValue >= target;
      case '<':
        return recordValue < target;
      case '<=':
        return recordValue <= target;
      default:
        return true;
    }
  },
});

// ---------- Table + export ----------

const exportJsonToExcel = <T extends object>(
  jsonData: T[],
  fileName: string
): void => {
  const worksheet = XLSX.utils.json_to_sheet(jsonData);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');
  const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
  const data = new Blob([excelBuffer], { type: 'application/octet-stream' });
  saveAs(data, `${fileName}.xlsx`);
};

const columnHeader = <T,>(column: SummaryColumn<T>): string =>
  typeof column.title === 'string' ? column.title : String(column.key ?? '');

const columnExportValue = <T,>(
  column: SummaryColumn<T>,
  record: T
): string | number => {
  if (column.exportValue) return column.exportValue(record);
  const { dataIndex } = column;
  if (typeof dataIndex === 'string' || typeof dataIndex === 'number') {
    const value = (record as Record<string | number, unknown>)[dataIndex];
    return value === null || value === undefined
      ? ''
      : (value as string | number);
  }
  return '';
};

/**
 * Converts rows to what the table shows: column titles as headers and
 * readable values (enum labels, formatted dates) instead of raw fields.
 */
const toExportRows = <T,>(
  rows: T[],
  columns: SummaryColumn<T>[]
): Record<string, string | number>[] =>
  rows.map((record) =>
    Object.fromEntries(
      columns.map((column) => [
        columnHeader(column),
        columnExportValue(column, record),
      ])
    )
  );

interface SummaryTableProps<T extends object> {
  rows: T[];
  columns: SummaryColumn<T>[];
  isLoading: boolean;
  exportFileName: string;
  rowKey: TableProps<T>['rowKey'];
  /** Extra buttons shown before the export button (e.g. "Save grades"). */
  extraActions?: React.ReactNode;
}

/** Table plus an export button that exports what's currently filtered. */
export const SummaryTable = <T extends object>({
  rows,
  columns,
  isLoading,
  exportFileName,
  rowKey,
  extraActions,
}: SummaryTableProps<T>) => {
  const { t } = useTranslation();
  // null = no filter/sort applied yet, so export falls back to all rows
  const [filteredRows, setFilteredRows] = useState<T[] | null>(null);

  return (
    <>
      <Table<T>
        rowKey={rowKey}
        columns={columns}
        dataSource={rows}
        loading={isLoading}
        scroll={{ x: 'max-content' }}
        onChange={(_pagination, _filters, _sorter, extra) =>
          setFilteredRows(extra.currentDataSource)
        }
      />
      <Space style={{ marginTop: '16px' }}>
        {extraActions}
        <Button
          onClick={() =>
            exportJsonToExcel(
              toExportRows(filteredRows ?? rows, columns),
              exportFileName
            )
          }
          type="primary"
          disabled={rows.length === 0}
        >
          {t('summaryTable.export')}
        </Button>
      </Space>
    </>
  );
};

// ---------- Labels ----------

/**
 * Turns a value name into readable text:
 * "SignedByLegalDepartment" -> "Signed by legal department",
 * "registeringCollaborationDocument" -> "Registering collaboration document".
 */
const humanizeEnumName = (name: string): string => {
  const words = name
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/([A-Z])([A-Z][a-z])/g, '$1 $2')
    .replace(/_/g, ' ')
    .trim()
    .split(/\s+/)
    // keep acronyms like "UBB" as they are, lowercase everything else
    .map((word) => (/^[A-Z]{2,}$/.test(word) ? word : word.toLowerCase()))
    .join(' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
};

/**
 * Returns a function that turns a value name (e.g. the status "NotStarted")
 * into a label: the translation at `${translationPrefix}.${name}` if there is
 * one, otherwise the humanized name ("Not started").
 *
 *   const translatedLabel = useTranslatedLabel();
 *   listFilterProps(rows, 'status', translatedLabel('summaryTable.processStatus'))
 */
export const useTranslatedLabel = () => {
  const { t } = useTranslation();

  return useCallback(
    (translationPrefix: string) =>
      (value: unknown): string => {
        const name = value === null || value === undefined ? '' : String(value);
        if (!name) return '';
        return t(`${translationPrefix}.${name}`, {
          defaultValue: humanizeEnumName(name),
        });
      },
    [t]
  );
};
